import { useState, useEffect, useRef, forwardRef, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import BASE_URL from "../../../assets/global/url";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../assets/table-style";
import swal from "sweetalert";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import { PaginationControls } from "../../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";
// Replaced by checkCutoffDate (utils/cutoffDateValidation)
// import { useDateValidation } from "../../../hooks/customHook/useDateValidation";
import { checkCutoffDate } from "../../../utils/cutoffDateValidation";
import {
  formatToTwoDecimal,
  parseNumber,
} from "../../../utils/numberFormatter";
import CustomDatePicker from "../../../components/CustomDatePicker";
import TypedDateInput from "../../../components/inputs/TypedDateInput";
import { constant_productCategory } from "../../../constants/productOptions";
import {
  AdjustmentCell,
  IndicatorCell,
} from "./components/InventoryCounting/CountingInput";

const CountingCreate = ({ authrztn }) => {
  const userLoggedID = useDecodeToken();
  const { id } = useParams();
  const navigate = useNavigate();
  // Replaced by checkCutoffDate (utils/cutoffDateValidation)
  // const { dateValidation } = useDateValidation();

  const [inventoryCounting, setInventoryCounting] = useState("");
  const [itemList, setItemList] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [status, setStatus] = useState("");
  const [countingDate, setCountingDate] = useState("");
  const [isEditable, setIsEditable] = useState(false);
  const [validated, setValidated] = useState(false);
  const [isCutoffPosted, setIsCutoffPosted] = useState(false);
  const [originalItemList, setOriginalItemList] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [warehouseName, setWarehouseName] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const [draftedRows, setDraftedRows] = useState([]);

  const debounceTimer = useRef(null);

  const inventoryCountingEndpoint = `${BASE_URL}/inventoryCounting`;
  const productListModalPagination = useServerPagination("about:blank", 10);

  const handleEdit = () => {
    setIsEditable((prev) => {
      return !prev;
    });
    setItemList(originalItemList);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    const formattedItemList = itemList.map((item) => ({
      ...item,
      actual_count: parseFloat(String(item.actual_count).replace(/,/g, "")),
      actual_price: parseFloat(String(item.actual_price).replace(/,/g, "")),
    }));

    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill the red text fields",
      });
    } else if (formattedItemList.length === 0) {
      swal({
        icon: "error",
        title: "No items to update",
        text: "Please add at least one item to update.",
      });
    } else {
      swal({
        title: "Update this Inventory?",
        text: "",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then(async (confirmed) => {
        if (confirmed) {
          try {
            // Separate existing items from newly added ones
            const existingItems = formattedItemList.filter(
              (item) => !item.isNew,
            );
            const newItems = formattedItemList
              .filter((item) => item.isNew)
              .map(({ isNew, ...rest }) => rest); // strip the isNew flag before sending

            const res = await axios.put(
              `${BASE_URL}/inventoryCounting/update/${id}`,
              {
                countingDate,
                remarks,
                itemList: existingItems,
                newItems,
                userLoggedID,
                countingId: inventoryCounting.inventory_counting_id,
              },
            );
            if (res.status === 200) {
              swal({
                title: "Success",
                text: res.data.message,
                icon: "success",
                button: "OK",
              }).then(() => {
                fetchInventoryCounting();
                setIsEditable(false);
              });
            }
          } catch (error) {
            if (error.response && error.response.status == 409) {
              swal({
                title: "Oopps!",
                text: "Action is prohibited because the date provided for the Counting Time has already passed the posted cutoff.",
                icon: "error",
                button: "OK",
              });
            }
          }
        }
      });
    }

    setValidated(true);
  };

  const fetchInventoryCounting = () => {
    axios
      .get(`${BASE_URL}/inventoryCounting/getInventoryCounting/${id}`)
      .then((res) => {
        setInventoryCounting(res.data);
        setRemarks(res.data.remarks);
        setCountingDate(res.data.counting_date.slice(0, 10));
        setIsCutoffPosted(res.data.isPosted);
        console.log(res.data);
        setStatus(res.data.status);
      })
      .catch((error) => {
        console.error("Error fetching inventory counting: ", error);
      });
  };

  const pagination = useServerPagination(
    `${BASE_URL}/inventoryCounting/getInventoryItemList/${id}`,
    10,
  );

  useEffect(() => {
    setItemList(pagination.data);
    setOriginalItemList(pagination.data);
    if (pagination.data.length > 0 && !warehouseId) {
      setWarehouseId(pagination.data[0].warehouse_id);
      setWarehouseName(pagination.data[0].warehouse?.name || "");
    }
  }, [pagination.data]);

  useEffect(() => {
    fetchInventoryCounting();
  }, [id]);

  const inventoryItemList = itemList.map((data, i) => ({
    ...data,
    key: i,
    product_id: data.product_id || "N/A",
    productId: data.product_id || "N/A",
    productName: data.product_list?.product_name || "N/A",
    warehouseName: data.warehouse?.name || "N/A",
    stock_quantity: data.system_quantity || 0,
    average_price: data.average_price,
    actual_price: data.actual_price,
    actual_count: data.actual_count,
  }));

  const handleActualAmountChange = (newValue, actualAmount, row) => {
    const cleanedValue = newValue === "" ? "0" : String(newValue);

    setItemList((prevList) =>
      prevList.map((item) => {
        if (item.product_id === row.product_id) {
          const updatedItem = { ...item, [actualAmount]: cleanedValue };

          if (
            actualAmount === "actual_count" &&
            parseNumber(cleanedValue) === 0
          ) {
            updatedItem.actual_price = "0";
          }
          return updatedItem;
        }
        return item;
      }),
    );
  };

  const canEditItems =
    authrztn.includes("InventoryCounting-Edit") ||
    authrztn.includes("InventoryCounting-PostApprovalEdit");

  const isDisabled = !canEditItems || !isEditable;

  const columns = [
    { name: "Warehouse", selector: (row) => row.warehouseName },
    { name: "Product Name", selector: (row) => row.productName },
    {
      name: "System Quantity",
      selector: (row) => formatToTwoDecimal(row.stock_quantity),
    },
    {
      name: "Actual Count",
      cell: (row) => (
        <IndicatorCell
          row={row}
          fieldName="actual_count"
          baseFieldName="stock_quantity"
          onActualAmountChange={handleActualAmountChange}
          disabled={isDisabled}
        />
      ),
    },
    {
      name: "Average Price",
      selector: (row) => formatToTwoDecimal(row.average_price),
    },
    {
      name: "Actual Price",
      cell: (row) => (
        <IndicatorCell
          row={row}
          fieldName="actual_price"
          baseFieldName="average_price"
          onActualAmountChange={handleActualAmountChange}
          disabled={isDisabled}
        />
      ),
    },
    {
      name: "Adjustment",
      cell: (row) => (
        <AdjustmentCell
          row={row}
          onActualAmountChange={handleActualAmountChange}
          disabled={isDisabled}
        />
      ),
    },
    ...(isEditable
      ? [
          {
            name: "Action",
            cell: (row) => (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveItem(row.productId)}
              >
                Remove
              </button>
            ),
          },
        ]
      : []),
  ];

  // Product columns for modal table
  const productColumns = [
    {
      name: (
        <div className="custom-checkbox">
          <input
            type="checkbox"
            className="form-check-input"
            checked={
              productListModalPagination.data.length > 0 &&
              productListModalPagination.data.every((item) =>
                draftedRows.includes(item.product_id),
              )
            }
            onChange={(e) => handleSelectAllRows(e.target.checked)}
          />
        </div>
      ),
      cell: (row) => (
        <div className="custom-checkbox">
          <input
            type="checkbox"
            className="form-check-input border border-secondary"
            checked={draftedRows.includes(row.product_id)}
            onChange={() => handleSelectRow(row.product_id)}
          />
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    { name: "Product Name", selector: (row) => row.product_name },
    { name: "Product Category", selector: (row) => row.product_category },
    {
      name: "Average Price",
      selector: (row) => formatToTwoDecimal(row.average_price),
    },
    {
      name: "Quantity",
      selector: (row) => formatToTwoDecimal(row.stock_quantity),
    },
  ];

  const handleCancel = () => {
    navigate("/inventory/inventory-counting");
  };

  // --- Modal helpers ---
  // Current product IDs in the item list (used to exclude them in the modal)
  const currentProductIds = itemList.map((item) => item.product_id);

  const getProductListModal = () => {
    productListModalPagination.updateHttpMethod("POST");
    productListModalPagination.updateApiUrl(
      `${inventoryCountingEndpoint}/products/summary/selectable`,
    );
    productListModalPagination.updateParams({
      warehouseId,
      productList: currentProductIds.map((id) => `'${id}'`),
    });
  };

  const handleShow = () => setShowModal(true);

  const handleClose = () => {
    setShowModal(false);
    setCategory("");
    setSearchTerm("");
    setDraftedRows([]);
  };

  const handleSelectRow = (productId) => {
    if (draftedRows.includes(productId)) {
      setDraftedRows((prev) => prev.filter((id) => id !== productId));
    } else {
      setDraftedRows((prev) => [...prev, productId]);
    }
  };

  const handleSelectAllRows = (isChecked) => {
    const ids = productListModalPagination.data.map((item) => item.product_id);
    if (isChecked) {
      setDraftedRows((prev) => [...prev, ...ids]);
    } else {
      setDraftedRows((prev) => prev.filter((id) => !ids.includes(id)));
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      productListModalPagination.updateHttpMethod("POST");
      productListModalPagination.updateApiUrl(
        `${inventoryCountingEndpoint}/products/summary/selectable/search`,
      );
      productListModalPagination.updateParams({
        warehouseId,
        productList: currentProductIds.map((id) => `'${id}'`),
        searchText: value,
        productCategory: category,
      });
    }, 500);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setCategory("");
  };

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
  };

  const handleSubmitModal = () => {
    const newItems = productListModalPagination.data
      .filter((item) => draftedRows.includes(item.product_id))
      .map((item) => ({
        product_id: item.product_id,
        product_list: { product_name: item.product_name },
        warehouse: { name: warehouseName },
        system_quantity: item.stock_quantity ?? 0,
        average_price: item.average_price ?? 0,
        actual_price: item.average_price ?? 0,
        actual_count: item.stock_quantity ?? 0,
        warehouse_id: warehouseId,
        isNew: true,
      }));

    setItemList((prev) => [...prev, ...newItems]);
    setShowModal(false);
    setSearchTerm("");
    setDraftedRows([]);
  };

  // Remove an item from the list (only while editing)
  const handleRemoveItem = (productId) => {
    setItemList((prev) => prev.filter((item) => item.product_id !== productId));
  };

  useEffect(() => {
    console.log("Iteem ", itemList);
  }, [itemList]);

  const handleApprove = async (e) => {
    e.preventDefault();

    if (itemList.length === 0) {
      swal({
        icon: "error",
        title: "No items to approve",
        text: "Please add at least one item to approve.",
      });
      return;
    }

    swal({
      title: "Approve this request?",
      text: "Are you sure you want to approve",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        const data = {
          countingDate,
          remarks,
          itemList: itemList.map((item) => ({
            productId: item.product_list.product_id,
            warehouseId: item.warehouse_id,
            quantity: item.system_quantity || 0,
            actualCount:
              parseFloat(String(item.actual_count).replace(/,/g, "")) || 0,
            actualPrice:
              parseFloat(String(item.actual_price).replace(/,/g, "")) || 0,
            transaction_id: inventoryCounting.transaction_id,
          })),
          transactionId: inventoryCounting.transaction_id,
          inventoryCountingId: id,
          userLoggedID,
        };
        axios
          .post(`${BASE_URL}/inventoryCounting/transactions/approve`, data)
          .then((res) => {
            if (res.status === 200) {
              swal({
                title: "Request Approved Successfully",
                text: "The request has been successfully approved.",
                icon: "success",
              }).then(() => {
                navigate("/inventory/inventory-counting");
              });
            } else if (res.status === 201) {
              swal({
                title: res.data.message,
                text: "Please create a cutoff first",
                icon: "error",
              });
            } else {
              swal({
                title: "Something Went Wrong",
                text: "Please contact your support immediately",
                icon: "error",
                dangerMode: true,
              });
            }
          })
          .catch((error) => {
            if (error.response.status === 401) {
              swal({
                icon: "error",
                // title: "Please contact your support",
                title: error.response.data.title,
                text: error.response.data.message,
              }); // test 1
            } else {
              if (error.response && error.response.status == 409) {
                swal({
                  title: "Oopps!",
                  text: "Action is prohibited because the date provided for the Counting Time has already passed the posted cutoff.",
                  icon: "error",
                  button: "OK",
                });
                return;
              }
              swal({
                title: "Something Went Wrong",
                text: "Please contact your support immediately",
                icon: "error",
                dangerMode: true,
              });
              console.error("Error approving request:", error);
            }
          });
      }
    });
  };

  const handleReject = async (e) => {
    e.preventDefault();
    swal({
      title: "Reject this request?",
      text: "Are you sure you want to reject",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        axios
          .put(`${BASE_URL}/inventoryCounting/rejectInventoryCounting`, {
            id,
            userLoggedID,
            countingDate,
          })
          .then((res) => {
            if (res.status === 200) {
              swal({
                title: "Request Rejected Successfully",
                text: "The request has been successfully rejected.",
                icon: "success",
                successMode: true,
              }).then(() => {
                navigate("/inventory/inventory-counting");
              });
            } else {
              swal({
                title: "Something Went Wrong",
                text: "Please contact your support immediately",
                icon: "error",
                dangerMode: true,
              });
            }
          })
          .catch((error) => {
            if (error.response.status === 401) {
              swal({
                icon: "error",
                // title: "Please contact your support",
                title: error.response.data.title,
                text: error.response.data.message,
              }); // test 1
            } else {
              if (error.response && error.response.status === 409) {
                swal({
                  title: "Oopps!",
                  text: "Action is prohibited because the date provided for the Counting Time has already passed the posted cutoff.",
                  icon: "error",
                  button: "OK",
                });
                return;
              }
              swal({
                title: "Something Went Wrong",
                text: "Please contact your support immediately",
                icon: "error",
                dangerMode: true,
              });
              console.error("Error rejecting request:", error);
            }
          });
      }
    });
  };

  // Custom input for DatePicker: typed as MM/dd/yyyy and shown as Oct/10/2026
  // (TypedDateInput), or picked from the calendar. Wrapped in useMemo so the
  // input isn't remounted on every render. Only a complete date reaches
  // handleDateChange, so the cutoff check doesn't run on a half-typed date.
  const CustomInput = useMemo(() => {
    const Input = forwardRef(
      (
        { value, onClick, onChange, onBlur, onFocus, onKeyDown, generateYears },
        ref,
      ) => {
        return (
          <TypedDateInput
            className="form-control p-3 w-100"
            onClick={(e) => {
              if (onClick) onClick(e);

              let selectedYear = new Date().getFullYear();
              if (value) {
                const parsed = new Date(value);
                // Extract year safely if value is a valid date
                if (!isNaN(parsed.getTime())) {
                  selectedYear = parsed.getFullYear();
                }
              }

              // Reset/Initialize Year List based on selected date
              if (generateYears && typeof generateYears === "function") {
                generateYears(selectedYear);
              }
            }}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            value={value}
            ref={ref}
            required
            disabled={!canEditItems || !isEditable}
          />
        );
      },
    );

    Input.displayName = "CustomInput";
    return Input;
  }, [canEditItems, isEditable]);

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3">
            <Link to="/inventory/inventory-counting" className="text-dark me-2">
              <i class="fa-solid fa-arrow-left"></i>
            </Link>
            INVENTORY COUNTING DETAILS
          </span>
        </div>
      </div>

      <Form noValidate validated={validated} onSubmit={handleUpdate}>
        <div className="container-fluid mt-3">
          <div className="row mx-auto">
            <div className="col-sm mb-3">
              <span>Counting ID</span>
              <input
                type="text"
                className="form-control custom-form-height border-0 custom-input-form-readOnly"
                readOnly
                value={inventoryCounting.transaction_id || ""}
              />
            </div>
            <div className="col-sm"></div>
            {/* <div className="col-sm"></div> */}
          </div>
          <div className="row mx-auto">
            <div className="col-sm mb-3">
              <span>Counting Time</span>

              {/* <input
                type="date"
                className="form-control custom-form-height"
                value={countingDate || ""}
                onChange={(e) => setCountingDate(e.target.value)}
                disabled={!authrztn.includes("InventoryCounting-Edit")}
                readOnly={!isEditable}
                required
              /> */}
              {/* <div className="position-relative">
                <DatePicker
                  selected={countingDate}
                  onChange={(date) => {
                    setCountingDate(date);
                    dateValidation(date, setCountingDate, "Counting Time");
                  }}
                  dateFormat="MMM/dd/yyyy"
                  className="form-control p-2"
                  customInput={<CustomInput />}
                />
                <i
                  class="fa-solid fa-calendar-week calendar-position"
                  style={{
                    right: `${validated ? "2rem" : "1rem"}`,
                    top: "1.3rem",
                  }}
                ></i>
              </div> */}
              <CustomDatePicker
                label={"Counting Time"}
                selected={countingDate ? new Date(countingDate) : ""}
                handleDateChange={(date) => {
                  setCountingDate(date);
                  // Replaced by checkCutoffDate (utils/cutoffDateValidation):
                  // useDateValidation's warning is commented out, so it never warned
                  // dateValidation(date, setCountingDate, "Counting Time");
                  // CustomInput (TypedDateInput) only sends complete dates
                  checkCutoffDate(date, "Counting Time");
                }}
                setter={setCountingDate}
                CustomInput={CustomInput}
                isRequired={true}
                validated={validated}
                // Replaced by checkCutoffDate (utils/cutoffDateValidation)
                // dateValidation={dateValidation}
                dateValidation={(date) => {
                  checkCutoffDate(date, "Counting Time");
                }}
                iconTopOffset={"1.3rem"}
                maxDate={new Date()}
              />
            </div>
            <div className="col-sm"></div>
            {/* <div className="col-sm"></div> */}
          </div>
          <div className="row mx-auto">
            <div className="col-sm mb-3">
              <span>Remarks</span>
              <textarea
                rows="5"
                className="form-control"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={!canEditItems}
                readOnly={!isEditable}
              ></textarea>
            </div>
            <div className="col-sm"></div>
          </div>
        </div>
        <div className="container-fluid">
          <div className="w-100 d-flex align-items-center mt-3 p-2">
            <h5>Item List</h5>
            <hr className="flex-grow-1 mx-3" />
          </div>
          <div className="w-100 mt-3 container-fluid">
            <DataTable
              columns={columns}
              data={inventoryItemList}
              customStyles={customStyles}
              className="dataTable"
            />
            <PaginationControls {...pagination} />
          </div>
          {isEditable && (
            <div className="w-100 d-flex justify-content-end pe-3 mt-3">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  getProductListModal();
                  handleShow();
                }}
              >
                New Item
              </button>
            </div>
          )}
        </div>

        <div className="container-fluid mt-5">
          <div className="row">
            <div className="col-sm ms-3">
              {/* {authrztn.includes("InventoryCounting-Edit") &&
                status !== "Approved" &&
                status !== "Rejected" &&
                isEditable == false && (
                  <button
                    type="button"
                    variant="outline-secondary"
                    onClick={handleEdit}
                    className="btn btn-outline-secondary px-5"
                  >
                    Edit
                  </button>
                )} */}
            </div>
            <div className="col-sm"></div>
            <div className="col-sm"></div>
            <div className="col-sm me-3">
              {authrztn.includes("InventoryCounting-Approve") &&
                status !== "Approved" &&
                status !== "Rejected" && (
                  <div className="row">
                    {isEditable === false && (
                      <>
                        <div className="col-sm">
                          <button
                            type="button"
                            onClick={handleEdit}
                            className="btn btn-primary w-100"
                          >
                            Edit
                          </button>
                        </div>
                        <div className="col-sm mb-2">
                          <button
                            onClick={handleReject}
                            disabled={isCutoffPosted}
                            className="btn btn-danger w-100"
                          >
                            Reject
                          </button>
                        </div>
                        <div className="col-sm">
                          <button
                            type="button"
                            onClick={handleApprove}
                            className="btn btn-success w-100"
                            disabled={status === "Approved" || isCutoffPosted}
                          >
                            Approve
                          </button>
                        </div>
                        {isCutoffPosted && (
                          <div>
                            <p className="text-danger">
                              Approval and rejection actions are prohibited
                              since the Counting time has already been posted.
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    {isEditable === true && (
                      <>
                        <div className="col-sm mb-2">
                          <button
                            type="button"
                            variant="outline-secondary"
                            onClick={handleEdit}
                            className="btn btn-secondary w-100"
                          >
                            Cancel
                          </button>
                        </div>
                        <div className="col-sm">
                          <button
                            type="submit"
                            // onClick={handleApprove} // Uncomment if needed
                            className="btn btn-primary w-100"
                            // disabled={status === "Approved"} // Uncomment if needed
                          >
                            Update
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

              {/* Post-approval edit: only shown once a counting is already
                  Approved, and only for roles granted this specific
                  permission - separate from the pre-approval Edit above. */}
              {authrztn.includes("InventoryCounting-PostApprovalEdit") &&
                status === "Approved" && (
                  <div className="row">
                    {isEditable === false && (
                      <>
                        <div className="col-sm">
                          <button
                            type="button"
                            onClick={handleEdit}
                            className="btn btn-primary w-100"
                            disabled={isCutoffPosted}
                          >
                            Edit
                          </button>
                        </div>
                        {isCutoffPosted && (
                          <div>
                            <p className="text-danger">
                              Editing is prohibited since the Counting time has
                              already been posted.
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    {isEditable === true && (
                      <>
                        <div className="col-sm mb-2">
                          <button
                            type="button"
                            variant="outline-secondary"
                            onClick={handleEdit}
                            className="btn btn-secondary w-100"
                          >
                            Cancel
                          </button>
                        </div>
                        <div className="col-sm">
                          <button
                            type="submit"
                            className="btn btn-primary w-100"
                          >
                            Update
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
            </div>
          </div>
        </div>
      </Form>
      {/* Add Item Modal */}
      <Modal
        show={showModal}
        onHide={handleClose}
        size="xl"
        backdrop="static"
        className="custom-modal-width"
      >
        <Modal.Header className="border-0" closeButton>
          <Modal.Title>INVENTORY | PRODUCT LIST</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container-fluid">
            <div className="row">
              <div className="col-sm mb-2">
                <span>Product Category</span>
                <select
                  id="categoryFilter"
                  className="form-select"
                  value={category}
                  onChange={handleCategoryChange}
                >
                  <option value="">All Categories</option>
                  {constant_productCategory.map((product, index) => (
                    <option key={index} value={product}>
                      {product}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm d-flex flex-row align-items-end mb-2 filter-btn-container w-100">
                <button
                  className="btn btn-secondary"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </button>
              </div>
              <div className="col-sm"></div>
            </div>
            <div className="w-100 mt-2 mb-2">
              <input
                id="searchBar"
                type="text"
                className="form-control"
                placeholder="Search by Product Name"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          <div className="w-100 mt-4 container-fluid">
            <DataTable
              columns={productColumns}
              data={productListModalPagination.data}
              customStyles={customStyles}
              onRowClicked={(row) => handleSelectRow(row.product_id)}
              pointerOnHover
            />
            <PaginationControls {...productListModalPagination} />
          </div>
          <Modal.Footer className="border-0 p-0 mt-5">
            <Button variant="secondary" type="button" onClick={handleClose}>
              Close
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleSubmitModal}
              disabled={draftedRows.length === 0}
            >
              Add Selected ({draftedRows.length})
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CountingCreate;
